"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight, Layers, FileCheck, Cpu, Activity, ShieldCheck, Sparkles, Check } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const phases = [{
  id: 1,
  phaseNumber: "Phase 01",
  title: "Feasibility & Optical Coupling Assessment",
  duration: "Weeks 1-3",
  description: "Initial optical path evaluations, non-destructive probe testing, and fiber-optic attenuation checks directly in customer process lines or laboratory slipstreams.",
  deliverables: "Optical coupling report, signal-to-noise baseline, and feasibility matrix"
}, {
  id: 2,
  phaseNumber: "Phase 02",
  title: "Chemometric Calibration & Method Development",
  duration: "Weeks 4-7",
  description: "Multivariate data modelling, principal component regression (PCR), and PLS calibration sets verified across representative batch spectra.",
  deliverables: "Validated chemometric model files, RMSEP variance calculations, and test sets"
}, {
  id: 3,
  phaseNumber: "Phase 03",
  title: "In-Line Vessel Integration & Hardware Interfacing",
  duration: "Weeks 8-10",
  description: "Sanitary flange mounting, flow cell integration, ATEX/hazardous zone isolation, and direct SCADA/OPC-UA communication alignment.",
  deliverables: "Mechanical interface schematics, automated communication loop verification"
}, {
  id: 4,
  phaseNumber: "Phase 04",
  title: "Qualification & Validation Protocols",
  duration: "Weeks 11-12",
  description: "Full regulatory documentation package, audit trail qualification, IQ/OQ execution, and comprehensive operator training on manufacturing lines.",
  deliverables: "Executed IQ/OQ packages, traceability matrix, and compliance sign-off"
}];
const highlights = ["Complete turn-key commissioning with localized Irish & UK engineering teams", "Zero-downtime hot-tap sanitary integration options for active reactors", "Comprehensive IQ/OQ validation protocols and execution support", "Dedicated chemometrician and validation support throughout system lifecycle"];
export default function ProcessTimeline() {
  const [activePhase, setActivePhase] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const phaseIcons = [Layers, Activity, Cpu, ShieldCheck];
  return <section id="implementationprocesstimeline" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 lg:mb-20">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5 px-3 py-1 font-medium">
            <Sparkles className="size-3.5 mr-1" />
            Structured Deployment Framework
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Structured Deployment for Critical Analytical Systems
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From initial optical coupling assessment to certified GAMP 5 compliance, our four-phase implementation process guarantees reproducible analytical measurement in regulated life sciences and manufacturing environments.
          </p>
        </div>

        {/* 5 / 7 Split Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          
          {/* Left Column: 4-Phase Timeline (5 cols) */}
          <div className="lg:col-span-5 flex flex-col space-y-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Implementation Workflow
              </span>
              <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                4 Key Phases
              </span>
            </div>

            <div className="relative pl-6 sm:pl-8 space-y-6 before:absolute before:left-3 sm:before:left-4 before:top-3 before:bottom-3 before:w-0.5 before:bg-border">
              {phases.map((phase, i) => {
              const isSelected = activePhase === i;
              const IconComponent = phaseIcons[i % phaseIcons.length];
              return <div key={phase.id} data-index={i} className="relative group cursor-pointer" onClick={() => setActivePhase(i)}>
                    {/* Timeline Node Indicator */}
                    <div className={`absolute -left-6 sm:-left-8 top-4 size-6 sm:size-8 rounded-full border-2 flex items-center justify-center transition-colors duration-200 ${isSelected ? "bg-primary border-primary text-primary-foreground shadow-sm shadow-primary/30" : "bg-background border-border text-muted-foreground group-hover:border-primary/60 group-hover:text-primary"}`}>
                      <span className="text-xs font-bold">{i + 1}</span>
                    </div>

                    {/* Timeline Card */}
                    <motion.div initial={shouldReduceMotion ? false : {
                  opacity: 0,
                  x: -16
                }} whileInView={shouldReduceMotion ? undefined : {
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: i * 0.1
                }}>
                      <Card className={`transition-[border-color,background-color,box-shadow] duration-200 py-4 px-5 border ${isSelected ? "border-primary/40 bg-secondary/50 shadow-xs ring-1 ring-primary/20" : "border-border bg-card hover:border-primary/30 hover:bg-muted/30"}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-primary uppercase tracking-wide">
                                {phase.phaseNumber}
                              </span>
                              <span className="text-xs text-muted-foreground font-mono">
                                • {phase.duration}
                              </span>
                            </div>
                            <h3 className="text-sm sm:text-base font-semibold text-foreground leading-snug">
                              {phase.title}
                            </h3>
                          </div>
                          <div className={`p-2 rounded-md transition-colors shrink-0 ${isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                            <IconComponent className="size-4" />
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  </div>;
            })}
            </div>
          </div>

          {/* Right Column: Supporting Text & Active Phase Deep Dive (7 cols) */}
          <div className="lg:col-span-7 space-y-8">
            <Card className="border-border bg-card shadow-sm overflow-hidden py-0">
              <div className="p-6 sm:p-8 bg-secondary/30 border-b border-border">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <Badge variant="default" className="bg-primary text-primary-foreground font-semibold px-3 py-1 text-xs">
                    Phase Breakdown
                  </Badge>
                  <span className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    <Activity className="size-4 text-primary" />
                    Target Timeline: {phases[activePhase].duration}
                  </span>
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                  {phases[activePhase].title}
                </h3>
                <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                  {phases[activePhase].description}
                </p>
              </div>

              <CardContent className="p-6 sm:p-8 space-y-6">
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                    <FileCheck className="size-4 text-primary" />
                    Key Phase Deliverables & Milestones
                  </h4>
                  <div className="p-4 rounded-lg bg-muted/40 border border-border/80 text-foreground text-sm leading-relaxed font-medium">
                    {phases[activePhase].deliverables}
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                    Engineering Standards & Quality Commitments
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {highlights.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 bg-background/60 p-3 rounded-md border border-border/50">
                        <div className="mt-0.5 rounded-full p-0.5 bg-primary/10 text-primary shrink-0">
                          <Check className="size-3.5" />
                        </div>
                        <span className="leading-snug">{item}</span>
                      </div>)}
                  </div>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border mt-6">
                  <div className="text-xs text-muted-foreground text-center sm:text-left">
                    Direct technical coordination across Ireland & the UK.
                  </div>
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <Button asChild variant="default" size="default" className="w-full sm:w-auto">
                      <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                        <span>Schedule Assessment</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="default" className="w-full sm:w-auto">
                      <Link to="/ServicesPage">
                        <span>All Services</span>
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bottom Support Banner */}
            <div className="rounded-xl p-5 border border-primary/20 bg-primary/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <div className="size-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                  <CheckCircle2 className="size-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground">
                    Regulated Life Sciences Assurance
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Qualified against ALCOA+ data integrity guidelines and local compliance frameworks.
                  </p>
                </div>
              </div>
              <Button asChild variant="ghost" size="sm" className="text-primary hover:text-primary hover:bg-primary/10 shrink-0">
                <Link to="/SolutionsPage" className="inline-flex items-center gap-1.5 text-xs font-semibold">
                  <span>Explore Solutions</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>

          </div>

        </div>
      </div>
    </section>;
}